import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Button, Chip, Checkbox, Dialog, Slide, MenuItem, Select,
  Typography, Grid, TextField, FormControl, FormLabel, IconButton
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { fetchData, putData } from "../../BaseUrl";
import DownloadIcon from "@mui/icons-material/Download";
import ProgressReportDetail from "./ProgressReportDetail";
import Toast from "../../DynamicComponent/Toast";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const fallbackRows = [
  { id: 1, studentName: "Aarav Kumar", regNo: "REG001", written: 25, oral: 17, homework: 18, project: 16, attendance: 9, overall: 85, grade: "A", applicationId: "APP001" },
  { id: 2, studentName: "Priya Sharma", regNo: "REG002", written: 28, oral: 19, homework: 20, project: 18, attendance: 10, overall: 95, grade: "A+", applicationId: "APP002" },
  { id: 3, studentName: "Karthik Raj", regNo: "REG003", written: 20, oral: 14, homework: 15, project: 13, attendance: 8, overall: 70, grade: "B", applicationId: "APP003" },
  { id: 4, studentName: "Divya Nair", regNo: "REG004", written: 22, oral: 15, homework: 16, project: 14, attendance: 8, overall: 75, grade: "B", applicationId: "APP004" },
  { id: 5, studentName: "Sanjay Patel", regNo: "REG005", written: 18, oral: 12, homework: 13, project: 11, attendance: 7, overall: 61, grade: "C", applicationId: "APP005" },
];

const gradeChipStyle = (grade) => {
  if (grade === "A+") return { backgroundColor: "#e8f5e9", color: "#4caf50" };
  if (grade === "A") return { backgroundColor: "#e3f2fd", color: "#1976d2" };
  if (grade === "B") return { backgroundColor: "#fff3e0", color: "#f57c00" };
  return { backgroundColor: "#ffebee", color: "#f44336" };
};

const actionBtnStyle = {
  border: "1px solid #D5D7DA",
  borderRadius: "8px",
  color: "#8F95B2",
  textTransform: "none",
  fontSize: "13px",
  padding: "6px 14px",
};

function EditProgressForm({ student, onClose, onSaved }) {
  const [form, setForm] = useState({
    written: student?.written ?? "",
    oral: student?.oral ?? "",
    homework: student?.homework ?? "",
    project: student?.project ?? "",
    attendance: student?.attendance ?? "",
  });
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const calcOverall = () => {
    const total = Number(form.written) + Number(form.oral) + Number(form.homework) + Number(form.project) + Number(form.attendance);
    return total;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const overall = calcOverall();
      const grade = overall >= 90 ? "A+" : overall >= 80 ? "A" : overall >= 70 ? "B" : overall >= 60 ? "C" : "D";
      const payload = { ...form, overall, grade };
      if (student?.id) {
        await putData(`teacher/progress/${student.id}`, payload);
      }
      setToast({ open: true, severity: "success", message: "Marks updated successfully" });
      setTimeout(() => { onSaved(); onClose(); }, 1200);
    } catch (_) {
      setToast({ open: true, severity: "error", message: "Update failed" });
    }
  };

  if (!student) return null;

  const labelStyle = { color: "#8F95B2", fontSize: "13px" };
  const fields = [
    { name: "written", label: "Written (30)", max: 30 },
    { name: "oral", label: "Oral (20)", max: 20 },
    { name: "homework", label: "Homework (20)", max: 20 },
    { name: "project", label: "Project (20)", max: 20 },
    { name: "attendance", label: "Attendance (10)", max: 10 },
  ];

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography sx={{ fontWeight: "bold", fontSize: "16px" }}>Edit Progress Report</Typography>
          <Typography sx={{ color: "#8F95B2", fontSize: "13px" }}>{student.studentName} — {student.regNo}</Typography>
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>

      <Grid container spacing={2}>
        {fields.map(({ name, label, max }) => (
          <Grid item xs={6} key={name}>
            <FormControl fullWidth>
              <FormLabel sx={labelStyle}>{label}</FormLabel>
              <TextField
                size="small"
                name={name}
                type="number"
                value={form[name]}
                onChange={handleChange}
                inputProps={{ min: 0, max }}
                sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "#D5D7DA", borderRadius: "10px" } } }}
              />
            </FormControl>
          </Grid>
        ))}
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Overall (auto)</FormLabel>
            <TextField
              size="small"
              value={calcOverall()}
              disabled
              sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "#D5D7DA", borderRadius: "10px" }, backgroundColor: "#f5f5f5" } }}
            />
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3 }}>
        <Button onClick={onClose} sx={{ color: "#666", textTransform: "none", border: "1px solid #D5D7DA", borderRadius: "8px", px: 4 }}>Cancel</Button>
        <Button type="submit" sx={{ backgroundColor: "#1a237e", color: "#fff", textTransform: "none", borderRadius: "8px", px: 4, "&:hover": { backgroundColor: "#283593" } }}>Save</Button>
      </Box>
    </Box>
  );
}

export default function ProgressReport() {
  const [rows, setRows] = useState([]);
  const [activeTerm, setActiveTerm] = useState(1);
  const [selectedTerm, setSelectedTerm] = useState("Term 1");
  const [detailOpen, setDetailOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [editStudent, setEditStudent] = useState(null);
  const [selected, setSelected] = useState([]);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadProgress = useCallback(async (term) => {
    try {
      const response = await fetchData(`teacher/progress/list?term=${term}`);
      if (response?.data && Array.isArray(response.data)) {
        setRows(response.data);
      } else {
        setRows(fallbackRows);
      }
    } catch (_) {
      setRows(fallbackRows);
    }
  }, []);

  useEffect(() => {
    loadProgress(activeTerm);
  }, [loadProgress, activeTerm]);

  const toggleSelect = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const openDetail = (student) => {
    setSelectedStudent(student);
    setDetailOpen(true);
  };

  const openEdit = (student) => {
    setEditStudent(student);
    setEditOpen(true);
  };

  const terms = [1, 2, 3];

  const handleGenerate = async () => {
    setToast({ open: true, severity: "info", message: "Generating PDF…" });
    try {
      const ids = selected.length > 0 ? `&ids=${selected.join(",")}` : "";
      const res = await fetchData(`teacher/progress/pdf?term=${activeTerm}${ids}`);
      if (res?.downloadUrl) {
        const a = document.createElement("a");
        a.href = res.downloadUrl;
        a.download = `progress-report-term${activeTerm}.pdf`;
        a.click();
        setToast({ open: true, severity: "success", message: "PDF downloaded" });
      } else {
        window.print();
        setToast({ open: true, severity: "info", message: "Print dialog opened — save as PDF" });
      }
    } catch (_) {
      window.print();
      setToast({ open: true, severity: "info", message: "Print dialog opened — save as PDF" });
    }
  };

  return (
    <Box sx={{ padding: "1rem", textAlign: "left" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
      <p style={{ color: "rgba(0,0,0,0.5)", fontWeight: "bold", fontSize: "14px", margin: 0 }}>
        Pages / Progress Report
      </p>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1, mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <h2 style={{ margin: 0 }}>Progress Report</h2>
          <Chip label="105 Students" size="small" sx={{ backgroundColor: "#e8f5e9", color: "#4caf50", fontWeight: "bold", fontSize: "12px", borderRadius: "20px", padding: "4px 12px" }} />
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <Button startIcon={<DeleteOutlineIcon />} sx={actionBtnStyle}>Delete</Button>
          <Button startIcon={<FilterListIcon />} sx={actionBtnStyle}>Filters</Button>
          <Select
            size="small"
            value={selectedTerm}
            onChange={(e) => setSelectedTerm(e.target.value)}
            sx={{ fontSize: "13px", borderRadius: "8px", border: "1px solid #D5D7DA", height: "36px" }}
          >
            {["Term 1","Term 2","Term 3","Term 4"].map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
          <Button
            startIcon={<DownloadIcon />}
            onClick={handleGenerate}
            sx={{ background: "linear-gradient(90deg,#4A7BA8,#244C77)", color: "#fff", textTransform: "none", borderRadius: "8px", padding: "8px 20px", fontSize: "13px" }}>
            Generate PDF
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 0, mb: 2, border: "1px solid #D5D7DA", borderRadius: "8px", overflow: "hidden", width: "fit-content" }}>
        {terms.map((t) => (
          <Button
            key={t}
            onClick={() => setActiveTerm(t)}
            sx={{
              textTransform: "none", fontSize: "13px", borderRadius: 0, px: 3,
              backgroundColor: activeTerm === t ? "#244C77" : "transparent",
              color: activeTerm === t ? "#fff" : "#8F95B2",
              "&:hover": { backgroundColor: activeTerm === t ? "#244C77" : "#f0f0f0" },
            }}
          >
            Term {t}
          </Button>
        ))}
      </Box>

      <Box sx={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0 8px" }}>
          <thead>
            <tr style={{ backgroundColor: "#8F95B2" }}>
              {["", "Student Name", "Reg No", "Written(30)", "Oral(20)", "Homework(20)", "Project(20)", "Attendance(10)", "Overall", "Grade", "Actions"].map((h, i) => (
                <th key={i} style={{ color: "#fff", padding: "12px", textAlign: "left", fontWeight: "bold", fontSize: "13px",
                  borderRadius: i === 0 ? "8px 0 0 0" : i === 10 ? "0 8px 0 0" : "0" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} style={{ backgroundColor: "#fff" }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#DAD8FC"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#fff"; }}>
                <td style={{ padding: "12px" }}>
                  <Checkbox size="small" checked={selected.includes(row.id)} onChange={() => toggleSelect(row.id)} />
                </td>
                <td style={{ padding: "12px", color: "#8F95B2", fontSize: "13px" }}>{row.studentName}</td>
                <td style={{ padding: "12px", color: "#8F95B2", fontSize: "13px" }}>{row.regNo}</td>
                <td style={{ padding: "12px", color: "#8F95B2", fontSize: "13px" }}>{row.written}</td>
                <td style={{ padding: "12px", color: "#8F95B2", fontSize: "13px" }}>{row.oral}</td>
                <td style={{ padding: "12px", color: "#8F95B2", fontSize: "13px" }}>{row.homework}</td>
                <td style={{ padding: "12px", color: "#8F95B2", fontSize: "13px" }}>{row.project}</td>
                <td style={{ padding: "12px", color: "#8F95B2", fontSize: "13px" }}>{row.attendance}</td>
                <td style={{ padding: "12px", color: "#333", fontSize: "13px", fontWeight: "bold" }}>{row.overall}%</td>
                <td style={{ padding: "12px" }}>
                  <Chip label={row.grade} size="small" sx={{ ...gradeChipStyle(row.grade), fontWeight: "bold", fontSize: "12px", borderRadius: "20px" }} />
                </td>
                <td style={{ padding: "12px" }}>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Button size="small" startIcon={<EditIcon />} onClick={() => openEdit(row)} sx={{ backgroundColor: "#e3f2fd", color: "#1976d2", borderRadius: "6px", textTransform: "none", fontSize: "12px", px: 1 }}>Edit</Button>
                    <Button size="small" startIcon={<VisibilityIcon />} onClick={() => openDetail(row)} sx={{ backgroundColor: "#f3e5f5", color: "#336599", borderRadius: "6px", textTransform: "none", fontSize: "12px", px: 1 }}>View</Button>
                  </Box>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>

      {/* View Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} TransitionComponent={Transition} fullWidth maxWidth="md">
        <ProgressReportDetail open={detailOpen} onClose={() => setDetailOpen(false)} student={selectedStudent} />
      </Dialog>

      {/* Edit Marks Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} TransitionComponent={Transition} fullWidth maxWidth="sm">
        <EditProgressForm
          student={editStudent}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            loadProgress(activeTerm);
            setToast({ open: true, severity: "success", message: "Progress report updated" });
          }}
        />
      </Dialog>
    </Box>
  );
}
